// /reports/gstr1 — GSTR1 Export (PAGES.md §3.42)
//
// Synchronous: reportsApi.generateGstr1 → GET /bills/reports/gstr1. gst-parked
// 070 (D8): the API files COMPLETED / RETURNED bills into B2B (valid customer
// GSTIN), B2CL (inter-state above the limit, unregistered buyer), B2CS (the
// rest of B2C, by state × rate), CDNR and CDNUR (notes on B2B / B2CL invoices).
// The CSV export is one GST-portal file per section. Periods with no invoices
// fall through to the empty state cleanly.

import { useState, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, RadioGroup, Alert, DataTable } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import {
  ReportPageShell,
  ReportKpiRow,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  reportsApi,
  downloadCsv,
  shareReportLink,
  KpiIcons,
  gstr1PortalFiles,
} from '@/features/reports';
import type { Gstr1Section, Gstr1Invoice, Gstr1Note, Gstr1B2csRow } from '@/features/reports';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { useAuthStore } from '@/stores/use-auth-store';

type SectionRow = Gstr1Section & { id: string };

// One row per invoice or note filed one by one (B2B, B2CL, CDNR, CDNUR).
type DocumentRow = {
  id: string;
  section: 'B2B' | 'B2CL' | 'CDNR' | 'CDNUR';
  number: string;
  date: string;
  buyer_gstin: string | null;
  place_of_supply: string | null;
  value: number;
  taxable_value: number;
  total_tax: number;
};

type B2csRow = Gstr1B2csRow & { id: string };

const SECTION_PILL: Record<DocumentRow['section'], string> = {
  B2B: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  B2CL: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  CDNR: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
  CDNUR: 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300',
};

const invoiceRow = (section: 'B2B' | 'B2CL') => (inv: Gstr1Invoice): DocumentRow => ({
  id: `${section}-${inv.bill_id}`,
  section,
  number: inv.invoice_number,
  date: inv.invoice_date,
  buyer_gstin: inv.buyer_gstin,
  place_of_supply: inv.place_of_supply_name,
  value: inv.invoice_value,
  taxable_value: inv.taxable_value,
  total_tax: inv.total_tax,
});

const noteRow = (section: 'CDNR' | 'CDNUR') => (n: Gstr1Note): DocumentRow => ({
  id: `${section}-${n.bill_id}`,
  section,
  number: n.note_number,
  date: n.note_date,
  buyer_gstin: n.buyer_gstin,
  place_of_supply: n.place_of_supply_name,
  value: n.note_value,
  taxable_value: n.taxable_value,
  total_tax: n.total_tax,
});

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// GSTR1 is an India-only statutory filing surface — GCC VAT regimes have no
// equivalent return, so the page is gated (not relabelled). Currency is
// immutable per tenant, so the regime is stable for the session and the
// redirect decision never changes mid-mount. The gate lives in a thin wrapper
// so the inner component's hook order is unconditional. INR → `showGstr1:
// true`, byte-identical to before. Sidebar/nav gating is handled in router.tsx.
export function Gstr1ExportPage() {
  const tax = useTaxRegime();
  // gst-parked 090 (D5): only a GST-registered business files GSTR-1. An
  // unregistered one charges no GST, so the page says why instead of a report.
  // `false` only — an older API that sends nothing keeps the report.
  const notRegistered = useAuthStore((s) => s.profile?.tenant?.gst_registered === false);
  if (!tax.showGstr1) return <Navigate to="/reports" replace />;
  if (notRegistered) return <Gstr1NotRegistered />;
  return <Gstr1ExportPageContent />;
}

function Gstr1NotRegistered() {
  return (
    <ReportPageShell
      title="GSTR1 Export"
      subtitle="GST portal CSVs for GST-registered businesses."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'GSTR1' }]}
      accent="indigo-violet"
    >
      <div className="animate-fade-in rounded-xl border border-warning-200 bg-warning-50 p-6 shadow-md dark:border-warning-800 dark:bg-warning-900/20">
        <h2 className="text-lg font-semibold text-warning-800 dark:text-warning-200">
          GSTR-1 is not available — your business is not GST registered
        </h2>
        <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
          Your bills charge no GST and print &ldquo;Not GST registered&rdquo;, so there is nothing to file.
          When you register, add your GSTIN in Business Settings. Bills made after that charge GST and show up here.
        </p>
        <Link
          to="/settings"
          className="mt-4 inline-flex items-center rounded-lg bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-200 hover:shadow-lg"
        >
          Open Business Settings
        </Link>
      </div>
    </ReportPageShell>
  );
}

function Gstr1ExportPageContent() {
  const [period, setPeriod] = useState<string>(currentMonth());
  const [periodType, setPeriodType] = useState<'monthly' | 'quarterly'>('monthly');
  const currency = useTenantCurrency();
  const subunit = getSubunitMultiplier(currency);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'gstr1', period, periodType],
    queryFn: () => reportsApi.generateGstr1(period, periodType),
    staleTime: 30_000,
  });

  const sections: SectionRow[] = useMemo(
    () => (data?.sections ?? []).map((s) => ({ ...s, id: s.key })),
    [data],
  );

  const documents: DocumentRow[] = useMemo(
    () =>
      data
        ? [
            ...data.b2b.map(invoiceRow('B2B')),
            ...data.b2cl.map(invoiceRow('B2CL')),
            ...data.cdnr.map(noteRow('CDNR')),
            ...data.cdnur.map(noteRow('CDNUR')),
          ]
        : [],
    [data],
  );
  const b2cs: B2csRow[] = useMemo(
    () => (data?.b2cs ?? []).map((r) => ({ ...r, id: `${r.place_of_supply ?? '-'}-${r.rate}` })),
    [data],
  );

  const totalInvoices = data?.total_invoices ?? 0;
  const totalTaxable = sections.reduce((sum, s) => sum + s.taxable_value, 0);
  const totalTax = sections.reduce((sum, s) => sum + s.total_tax, 0);

  const columns: ColumnDef<SectionRow>[] = [
    { key: 'label', header: 'Section', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.label}</span> },
    { key: 'invoice_count', header: 'Invoices / Notes', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.invoice_count.toLocaleString('en-IN')}</span> },
    { key: 'taxable_value', header: 'Taxable Value', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.taxable_value, currency)}</span> },
    { key: 'total_tax', header: 'Total Tax', numeric: true, render: (_v, row) => <span className="font-semibold tabular-nums">{formatPaise(row.total_tax, currency)}</span> },
  ];

  const documentColumns: ColumnDef<DocumentRow>[] = [
    { key: 'section', header: 'Section', render: (_v, row) => <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${SECTION_PILL[row.section]}`}>{row.section}</span> },
    { key: 'number', header: 'Number', render: (_v, row) => <span className="font-mono text-xs text-neutral-900 dark:text-neutral-100">{row.number}</span> },
    { key: 'date', header: 'Date', render: (_v, row) => <span className="tabular-nums">{row.date}</span> },
    { key: 'buyer_gstin', header: 'Buyer GSTIN', render: (_v, row) => <span className="font-mono text-xs">{row.buyer_gstin ?? '—'}</span> },
    { key: 'place_of_supply', header: 'Place of Supply', render: (_v, row) => <span>{row.place_of_supply ?? '—'}</span> },
    { key: 'value', header: 'Value', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.value, currency)}</span> },
    { key: 'taxable_value', header: 'Taxable Value', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.taxable_value, currency)}</span> },
    { key: 'total_tax', header: 'Tax', numeric: true, render: (_v, row) => <span className="font-semibold tabular-nums">{formatPaise(row.total_tax, currency)}</span> },
  ];

  const b2csColumns: ColumnDef<B2csRow>[] = [
    { key: 'place_of_supply_name', header: 'Place of Supply', render: (_v, row) => <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.place_of_supply_name ?? '—'}</span> },
    { key: 'rate', header: 'Rate', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.rate}%</span> },
    { key: 'taxable_value', header: 'Taxable Value', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.taxable_value, currency)}</span> },
    { key: 'total_tax', header: 'Tax', numeric: true, render: (_v, row) => <span className="font-semibold tabular-nums">{formatPaise(row.total_tax, currency)}</span> },
  ];

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      // One GST-portal file per non-empty section (b2b, b2cl, b2cs, cdnr, cdnur).
      if (!data) return;
      for (const file of gstr1PortalFiles(data, subunit)) {
        downloadCsv(
          `gstr1-${period}-${file.section}.csv`,
          file.headers.map((h) => ({ header: h, get: (r: Record<string, string | number>) => r[h] })),
          file.rows,
        );
      }
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, `GSTR1 ${period}`, `Invoices: ${totalInvoices} • Taxable: ${formatPaise(totalTaxable, currency)} • Tax: ${formatPaise(totalTax, currency)}`);
    }
  };

  return (
    <ReportPageShell
      title="GSTR1 Export"
      subtitle="Generate GST portal CSVs for B2B, B2CL, B2CS and credit / debit notes (CDNR, CDNUR), one file per section."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'GSTR1' }]}
      accent="indigo-violet"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={sections.length === 0} />}
      filterBar={
        <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div>
              <label htmlFor="gstr1-period" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">Filing Period</label>
              <input
                id="gstr1-period"
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="rounded-lg border-0 bg-neutral-50 dark:bg-neutral-900 px-4 py-2 text-sm text-neutral-900 dark:text-white ring-1 ring-neutral-200 dark:ring-neutral-700 focus:ring-2 focus:ring-brand-500 focus:outline-none tabular-nums"
              />
            </div>
            <div>
              <RadioGroup
                label="Period Type"
                value={periodType}
                onChange={(v) => setPeriodType(v as 'monthly' | 'quarterly')}
                variant="horizontal"
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                ]}
              />
            </div>
            <div className="lg:ml-auto">
              <Button variant="primary" onClick={() => refetch()} loading={isFetching}>Generate GSTR1</Button>
            </div>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={3} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          {data && data.incomplete_b2b_count > 0 ? (
            <div className="mb-6">
              <Alert
                variant="warning"
                title="Customer GSTIN not valid"
                description={`${data.incomplete_b2b_count} invoice(s) have a customer GSTIN that is not valid, so they are filed as B2C: ${data.warnings.map((w) => w.bill_number).slice(0, 5).join(', ')}${data.warnings.length > 5 ? ', …' : ''}. Fix the GSTIN on the customer to file them as B2B.`}
              />
            </div>
          ) : null}

          <ReportKpiRow
            tiles={[
              { id: 'invoices', title: 'Total Invoices', value: totalInvoices.toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.file },
              { id: 'taxable', title: 'Taxable Value', value: formatPaise(totalTaxable, currency), tone: 'brand', icon: KpiIcons.rupee },
              { id: 'tax', title: 'Total Tax', value: formatPaise(totalTax, currency), tone: 'violet', icon: KpiIcons.percent },
            ]}
          />

          <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6">
            <header className="border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-indigo-500 to-violet-500" />
                Section Preview
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">B2B, B2CL, B2CS and credit / debit notes before export. Credit notes are negative.</p>
            </header>
            <DataTable<SectionRow>
              columns={columns}
              data={sections}
              emptyState={{ title: 'No invoices for selected period', description: 'Select a period with sales activity to generate GSTR1.' }}
            />
          </section>

          {documents.length > 0 ? (
            <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6">
              <header className="border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4">
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                  <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-violet-500 to-pink-500" />
                  Invoices and Notes Filed One by One
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">B2B, B2CL, CDNR and CDNUR list every invoice or note.</p>
              </header>
              <DataTable<DocumentRow> columns={documentColumns} data={documents} />
            </section>
          ) : null}

          {b2cs.length > 0 ? (
            <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6">
              <header className="border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4">
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                  <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-brand-500 to-indigo-500" />
                  B2CS by Place of Supply and Rate
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Other B2C sales, with their credit notes netted off.</p>
              </header>
              <DataTable<B2csRow> columns={b2csColumns} data={b2cs} />
            </section>
          ) : null}
        </>
      )}
    </ReportPageShell>
  );
}
